import {
  WidgetOutboundClientPredicateGroup,
  WidgetOutboundPredicateGroup,
  WidgetOutboundPredicate,
  WidgetOutboundPredicateGroupType,
  WidgetOutboundPredicateComparison,
} from "../../types";

type PredicateObject = Record<string, string | number | undefined>;
type Comparator = (actual: string, expected: string) => boolean;

const STRING_COMPARATORS: Partial<
  Record<WidgetOutboundPredicateComparison, Comparator>
> = {
  [WidgetOutboundPredicateComparison.CONTAINS]: (a, e) => a.includes(e),
  [WidgetOutboundPredicateComparison.DOES_NOT_CONTAIN]: (a, e) =>
    !a.includes(e),
  [WidgetOutboundPredicateComparison.IS]: (a, e) => a === e,
  [WidgetOutboundPredicateComparison.IS_NOT]: (a, e) => a !== e,
  [WidgetOutboundPredicateComparison.STARTS_WITH]: (a, e) => a.startsWith(e),
  [WidgetOutboundPredicateComparison.ENDS_WITH]: (a, e) => a.endsWith(e),
};

const NUMERIC_COMPARATORS: Partial<
  Record<WidgetOutboundPredicateComparison, Comparator>
> = {
  [WidgetOutboundPredicateComparison.EQ]: (a, e) =>
    parseInt(a, 10) === parseInt(e, 10),
  [WidgetOutboundPredicateComparison.GT]: (a, e) =>
    parseInt(a, 10) > parseInt(e, 10),
  [WidgetOutboundPredicateComparison.LT]: (a, e) =>
    parseInt(a, 10) < parseInt(e, 10),
  [WidgetOutboundPredicateComparison.GTE]: (a, e) =>
    parseInt(a, 10) >= parseInt(e, 10),
  [WidgetOutboundPredicateComparison.LTE]: (a, e) =>
    parseInt(a, 10) <= parseInt(e, 10),
};

const COMPARATORS: Partial<
  Record<WidgetOutboundPredicateComparison, Comparator>
> = {
  ...STRING_COMPARATORS,
  ...NUMERIC_COMPARATORS,
};

export default class WidgetPredicateGroupEvaluator {
  private object: PredicateObject;

  constructor(object: PredicateObject) {
    this.object = object;
  }

  static matches(
    object: PredicateObject,
    predicateGroup: WidgetOutboundClientPredicateGroup,
  ): boolean {
    return new WidgetPredicateGroupEvaluator(object).matches(predicateGroup);
  }

  matches(predicateGroup: WidgetOutboundClientPredicateGroup): boolean {
    return predicateGroup.predicates.every((group) => this.groupMatches(group));
  }

  private groupMatches(group: WidgetOutboundPredicateGroup): boolean {
    if (group.predicates.length === 0) return true;

    if (group.type === WidgetOutboundPredicateGroupType.AND) {
      return group.predicates.every((predicate) =>
        this.predicateMatches(predicate),
      );
    } else {
      return group.predicates.some((predicate) =>
        this.predicateMatches(predicate),
      );
    }
  }

  private predicateMatches(predicate: WidgetOutboundPredicate): boolean {
    const actual = String(this.object[predicate.attribute] ?? "");
    const comparator = COMPARATORS[predicate.comparison];
    return comparator ? comparator(actual, predicate.value) : false;
  }
}
